import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let timer;
    const poll = async () => {
      if (attempts.current >= 8) {
        setStatus("failed");
        return;
      }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setStatus("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setStatus("failed");
          return;
        }
      } catch {
        /* keep polling */
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div className="bg-card border border-border rounded-2xl p-10 max-w-md w-full text-center fade-up" data-testid="payment-success-page">
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-primary animate-spin mb-4" />
            <h1 className="font-heading text-2xl font-black">Confirming your payment…</h1>
            <p className="text-muted-foreground mt-1">Hang tight, this only takes a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto text-secondary mb-4" />
            <h1 className="font-heading text-2xl font-black">You're Premium! 🎉</h1>
            <p className="text-muted-foreground mt-1 mb-6">Unlimited logging and full history are unlocked.</p>
            <Button onClick={() => navigate("/")} className="rounded-full font-bold w-full h-11" data-testid="success-continue-btn">Back to tracking</Button>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle className="w-14 h-14 mx-auto text-destructive mb-4" />
            <h1 className="font-heading text-2xl font-black">Couldn't confirm payment</h1>
            <p className="text-muted-foreground mt-1 mb-6">If you were charged, your access will update shortly.</p>
            <Button onClick={() => navigate("/pricing")} variant="outline" className="rounded-full font-bold w-full h-11" data-testid="success-retry-btn">Back to pricing</Button>
          </>
        )}
      </div>
    </div>
  );
}
